import React, { useState } from 'react';
import { IconX } from '@tabler/icons-react';
import { cn } from '@/utils/cn';
import { TeeInput } from '@/components/ui/tee-input';

export interface TeeTagInputProps {
  values: string[];
  onChange: (values: string[]) => void;
  label?: string;
  hint?: string;
  placeholder?: string;
  disabled?: boolean;
  maxTags?: number;
  className?: string;
}

export const TeeTagInput: React.FC<TeeTagInputProps> = ({
  values,
  onChange,
  label,
  hint,
  placeholder,
  disabled = false,
  maxTags,
  className,
}) => {
  const [draft, setDraft] = useState('');

  const commitDraft = () => {
    const trimmed = draft.trim();
    if (!trimmed) {
      setDraft('');
      return;
    }
    const alreadyExists = values.some(
      (v) => v.toLowerCase() === trimmed.toLowerCase()
    );
    if (alreadyExists) {
      setDraft('');
      return;
    }
    if (maxTags !== undefined && values.length >= maxTags) {
      setDraft('');
      return;
    }
    onChange([...values, trimmed]);
    setDraft('');
  };

  const removeTag = (tag: string) => {
    onChange(values.filter((v) => v !== tag));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitDraft();
      return;
    }
    if (e.key === ',') {
      e.preventDefault();
      commitDraft();
      return;
    }
    if (e.key === 'Backspace' && draft === '' && values.length > 0) {
      removeTag(values[values.length - 1]);
    }
  };

  const handleBlur = () => {
    if (draft.trim()) {
      commitDraft();
    }
  };

  return (
    <div className={cn('flex w-full flex-col gap-1.5', className)}>
      <TeeInput
        label={label}
        hint={hint}
        placeholder={placeholder}
        value={draft}
        disabled={disabled}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
      />

      {values.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {values.map((tag) => (
            <span
              key={tag}
              className="flex items-center gap-1 rounded-[6px] bg-[var(--background-badge-gray)] px-2 py-1 text-[14px] text-[var(--text-badge-gray)]"
            >
              {tag}
              <button
                type="button"
                aria-label={`Remove tag ${tag}`}
                onClick={() => removeTag(tag)}
                disabled={disabled}
                className="flex items-center"
              >
                <IconX size={12} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

TeeTagInput.displayName = 'TeeTagInput';

export default TeeTagInput;
